import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { ASC } from 'Angular/constants/core.constants';
import { OPEN_ITEM_MODAL_DEFAULT_SORT_DIRECTIONS } from 'Angular/features/components/saved-items/open-item-modal/constants';
import { SavedItemType } from 'Angular/features/components/saved-items/enums/saved-item-type.enum';
import { OpenItemModalAction } from 'Angular/features/components/saved-items/open-item-modal/enums/open-item-modal-action.enum';
import { OpenItemModalSortColumn } from 'Angular/features/components/saved-items/open-item-modal/enums/open-item-modal-sort-column.enum';
import { PublicSharingState } from 'SharingApp/ts/enums/public-sharing-state.enum';
import { FormattedShareableItem } from 'Angular/features/components/saved-items/open-item-modal/interfaces/serialized-shareable-item.interface';
import { ShareableItemTypeInfo } from 'Angular/features/components/saved-items/open-item-modal/interfaces/shareable-item-type-info-map.interface';
import { SortColumnAndDirection } from 'Angular/interfaces/sort-column-and-direction.interface';
import { SortDirectionCssClasses } from 'Angular/utils/sort/interfaces/sort-direction-css-classes.interface';
import { ShareableItem } from 'SharingApp/ts/interfaces/shareable-item.interface';

import { ConfirmModalComponent } from 'Angular/features/components/modals/confirm-modal/confirm-modal.component';
import { OpenItemPreviewModalComponent } from 'Angular/features/components/saved-items/open-item-modal/components/open-item-preview-modal/open-item-preview-modal.component';
import {
    getNewSortDirectionCssClasses,
    getSortColumnAndDirection,
    initializeSortDirectionCssClasses,
    sortTableItems,
} from 'Angular/utils/sort/table-sort';
import { isPublicShareableItem } from 'SharingApp/ts/utils/public-sharing-utils';
import { getSharingTooltipMessage } from 'SharingApp/ts/utils/sharing-utils';

/**
 * Open Item List Component
 *
 * Used as a subcomponent of the OpenItemModalComponent. This component displays a list of shareable items
 * and allows a user to select, edit, or delete items in the list.
 *
 * Input Properties:
 *  isOwner: boolean whether the user is the owner of the items in this list
 *  items: ShareableItem[] to be displayed in the list]
 *  itemTypeInfo: ShareableItemTypeInfo object that has the labels and whether the item type can be edited
 *  selectOnly: whether items in the list should be able to be edited/deleted or only selected.
 *  showRadioButtons: displays a radio button for each item in the list.
 *
 * Output Properties:
 *  itemActionHandler: EventEmitter that emits the item and the action type when the user performs an action on an item in the list
 *
 */
@Component({
    standalone: false,
    selector: 'ycn-open-item-list',
    templateUrl: './open-item-list.template.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenItemListComponent implements OnInit, OnChanges {
    @Input() isOwner: boolean = true;

    @Input() items: ShareableItem[] = [];

    @Input() itemTypeInfo: ShareableItemTypeInfo | null = { singularLabel: '', pluralLabel: '', isEditable: false };

    @Input() selectOnly: boolean = false;

    @Input() showRadioButtons: boolean = true;

    @Input() savedItemType!: SavedItemType;

    @Input() selectedItemId: number | null = null;

    @Output() itemActionHandler = new EventEmitter<{
        item: FormattedShareableItem | null;
        actionType: OpenItemModalAction;
    }>();

    @ViewChild('confirmDeleteModal') confirmDeleteModal!: ConfirmModalComponent;

    @ViewChild(OpenItemPreviewModalComponent) openItemPreviewModalComponent!: OpenItemPreviewModalComponent;

    confirmDeleteHeader: string = '';

    confirmDeleteText: string = '';

    formattedItems: FormattedShareableItem[] = [];

    itemToDelete: FormattedShareableItem | null = null;

    SORT_COLUMNS = OpenItemModalSortColumn;

    sortColumnAndDirection: SortColumnAndDirection<OpenItemModalSortColumn>;

    sortDirectionCssClasses: SortDirectionCssClasses = initializeSortDirectionCssClasses(
        Object.values(this.SORT_COLUMNS)
    );

    showOpenItemPreview: boolean = false;

    constructor() {
        this.sortColumnAndDirection = {
            sortColumn: OpenItemModalSortColumn.NAME,
            sortDirection: ASC,
        };
        this.sortDirectionCssClasses.name['th-sort-asc'] = true;
    }

    ngOnInit(): void {
        this.selectedItemId = null;
        this.confirmDeleteHeader = this.itemTypeInfo ? `Delete ${this.itemTypeInfo.singularLabel}` : '';
        this.confirmDeleteText = this.itemTypeInfo
            ? `Are you sure you want to delete this ${this.itemTypeInfo.singularLabel.toLowerCase()}?`
            : '';
        this.formattedItems = this.formatItems(this.items);
        this.formattedItems = sortTableItems<FormattedShareableItem, OpenItemModalSortColumn>(
            this.formattedItems,
            this.sortColumnAndDirection
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.items && changes.items.currentValue) {
            this.showOpenItemPreview =
                this.savedItemType === SavedItemType.FUND_CHART || this.savedItemType === SavedItemType.SCATTER_PLOT;
            this.formattedItems = this.formatItems(this.items);
            this.formattedItems = sortTableItems<FormattedShareableItem, OpenItemModalSortColumn>(
                this.formattedItems,
                this.sortColumnAndDirection
            );
        }
    }

    canDeleteItem(item: FormattedShareableItem): boolean {
        if (this.selectOnly || !this.isOwner) return false;
        return isPublicShareableItem(item) ? item.sharing.public_sharing_state === PublicSharingState.NOT_SHARED : true;
    }

    /**
     * Copies an array of shareable items while adding a tooltip message and the modify_date
     * converted to a date object for to each item.
     * @param items ShareableItem[] to format for display
     * @returns FormattedShareableItem[] used for displaying the list in the template
     */
    formatItems(items: ShareableItem[]): FormattedShareableItem[] {
        return items.map((item) => {
            return {
                ...item,
                // Store the modify_date as a date object so it can be used for sorting
                modifyDateObj: new Date(item.modify_date),
                tooltipMessage: getSharingTooltipMessage(item),
            };
        });
    }

    /**
     * Updates the sort direction and sort column based on the current sort and the column that was clicked.
     * @param newSortColumn
     */
    setSort(newSortColumn: OpenItemModalSortColumn): void {
        const prevSortColumn: OpenItemModalSortColumn = this.sortColumnAndDirection.sortColumn;
        this.sortColumnAndDirection = getSortColumnAndDirection<OpenItemModalSortColumn>(
            OPEN_ITEM_MODAL_DEFAULT_SORT_DIRECTIONS,
            prevSortColumn,
            newSortColumn,
            this.sortColumnAndDirection.sortDirection
        );
        this.sortDirectionCssClasses = getNewSortDirectionCssClasses<OpenItemModalSortColumn>(
            this.sortDirectionCssClasses,
            prevSortColumn,
            newSortColumn,
            this.sortColumnAndDirection.sortDirection
        );
        this.formattedItems = sortTableItems<FormattedShareableItem, OpenItemModalSortColumn>(
            this.formattedItems,
            this.sortColumnAndDirection
        );
    }

    editItem(item: FormattedShareableItem): void {
        this.itemActionHandler.emit({ item, actionType: OpenItemModalAction.EDIT });
    }

    selectItem(item: FormattedShareableItem): void {
        this.selectedItemId = item.id;
        this.itemActionHandler.emit({ item, actionType: OpenItemModalAction.SELECT });
    }

    openConfirmDeleteModal(item: FormattedShareableItem): void {
        this.itemToDelete = item;
        this.confirmDeleteModal.openModal();
    }

    confirmDeleteItem(confirmed: boolean): void {
        if (confirmed && this.itemToDelete) {
            this.itemActionHandler.emit({ item: this.itemToDelete, actionType: OpenItemModalAction.DELETE });
            this.itemToDelete = null;
        }
    }

    openSavedAssetPreviewModal(item: FormattedShareableItem): void {
        this.openItemPreviewModalComponent.openModal(item);
    }

    public clearSelection(): void {
        this.selectedItemId = null;
    }
}
